import React from 'react';

import classNames from 'classnames';

import SetupProgressBar from './SetupProgressBar';
import { STEP_GROUPS } from './stepGroups';
import StepStatusMarker from './StepStatusMarker';

// What the greyed-out row says when a step is not applicable: its module is
// off, or (Approvals) nothing switched on needs approving yet.
function notApplicableTag(step) {
    if (step.key === 'approvals') {
        return $t('nothing to approve yet');
    }

    if (step.key === 'proc' || step.key === 'radio') {
        return $t('module off');
    }

    return null;
}

function StepItem({ step, active, number, onSelect }) {
    const notApplicable = step.status === 'not_applicable';
    const tag = notApplicable ? notApplicableTag(step) : null;

    const handleClick = (clickEvent) => {
        if (notApplicable) {
            return;
        }

        clickEvent.preventDefault();
        onSelect(step.key);
    };

    return (
        <li>
            <a
                href={step.route}
                onClick={handleClick}
                aria-current={active ? 'step' : undefined}
                aria-disabled={notApplicable ? 'true' : undefined}
                title={notApplicable ? $t('Not applicable — the module or setting it configures is switched off') : undefined}
                className={classNames('tw-flex tw-items-center tw-gap-x-3 tw-px-3 tw-py-2 tw-rounded-lg tw-text-sm', {
                    'tw-bg-blue tw-text-white tw-font-medium': active,
                    'tw-text-charcoal hover:tw-bg-charcoal-10': !active && !notApplicable,
                    'tw-text-charcoal-50 tw-opacity-50 tw-cursor-default': notApplicable,
                })}
            >
                <StepStatusMarker step={step} active={active} number={number} />
                {/* Already localised server-side by SetupStepKey::label(). */}
                <span className="tw-text-start tw-flex-1 tw-min-w-0">{step.label}</span>
                {tag && <span className="tw-text-2xs tw-font-semibold tw-shrink-0">{tag}</span>}
            </a>
        </li>
    );
}

export default function StepRail({ steps, progress, currentStep, onSelect }) {
    // Numbered among the visible steps, as the "Step N of M" crumb counts them;
    // a not-applicable step has no number.
    const numbers = {};
    steps.filter((step) => step.status !== 'not_applicable').forEach((step, index) => (numbers[step.key] = index + 1));

    return (
        <nav aria-label={$t('Set-up steps')} className="tw-px-4 tw-py-5 tw-rounded-lg tw-bg-white tw-shadow-primary">
            <h2 className="tw-mx-2 tw-mb-2.5 tw-text-0.5sm tw-font-semibold tw-text-charcoal-50">{$t('Project setup')}</h2>
            <div className="tw-mx-2">
                <p className="tw-mb-1 tw-text-xs tw-text-charcoal-50">
                    {$t('{{completed}} of {{total}} complete', { completed: progress.completed, total: progress.total })}
                </p>
                <SetupProgressBar progress={progress} labelStyle="none" />
            </div>

            {STEP_GROUPS.map((group, index) => {
                const groupSteps = steps.filter((step) => step.group === group);

                if (groupSteps.length === 0) {
                    return null;
                }

                return (
                    <div key={group} className="tw-mt-4">
                        {index > 0 && (
                            <div className="tw-relative tw-mx-2 tw-mt-4 tw-mb-3 tw-border-t tw-border-dashed tw-border-charcoal-15">
                                <span className="tw-absolute tw-start-0 -tw-top-2.5 tw-bg-white tw-pe-2 tw-text-[11px] tw-font-semibold tw-text-green">
                                    {$t('Optional — do any time')}
                                </span>
                            </div>
                        )}
                        <ul className="tw-mt-2 tw-flex tw-flex-col tw-gap-y-0.5">
                            {groupSteps.map((step) => (
                                <StepItem key={step.key} step={step} active={step.key === currentStep} number={numbers[step.key] ?? '·'} onSelect={onSelect} />
                            ))}
                        </ul>
                    </div>
                );
            })}
        </nav>
    );
}
